import { AuditFields, CommonFields, CommonStatus, DocumentRef, PartnerRef, ValidAny } from "./common";
import { ExtractionLocation } from "./transformationTemplate";
import { TargetSchema } from "./document";

export type DocumentTransformationType =
  | "50_percent_report"
  | "medical_stoploss_claims"
  | "pharmacy_stoploss_claims"
  | "stoploss_claims"
  | "policy_document"
  | "reimbursements"
  | "unknown_format";

export enum DocumentTransformationStatus {
  PROCESSING = CommonStatus.PROCESSING,
  COMPLETED = CommonStatus.COMPLETED,
  FAILED = CommonStatus.FAILED,
  REVIEW_REQUIRED = CommonStatus.REVIEW_REQUIRED,
  LOADED = "loaded",
}

export type ExtractionMethod = "ocr_plus_ai" | "structured_parse" | "native_text" | "hybrid";

// Document Transformation Types

export type ExtractedRecordRule =
  | "insufficient_data"
  | "claimant_identification"
  | "policy_attribution"
  | "transformation_failed"
  | "unsupported_mapping_source"
  | "state_abbrev_invalid";

export interface ExtractedField {
  source: ExtractionLocation;
  target_schema: TargetSchema;
  target_field: string;
  extracted_value: ValidAny;
}

export interface ExtractedRecordError {
  schema?: TargetSchema;
  field?: string;
  /** Full extraction location for transformation errors (e.g. key, coordinates, row index) */
  source?: ExtractionLocation;
  message: string;
  rule?: ExtractedRecordRule;
}

export interface ExtractedRecord {
  extraction_method?: ExtractionMethod;
  extracted_fields?: ExtractedField[];
  validation_errors?: ExtractedRecordError[];
  transformation_errors?: ExtractedRecordError[];
  unused_data?: Record<string, ValidAny>;
  /**
   * ABSOLUTE persisted position of this record (#6755).
   *
   * Stamped once at ingress by `recordIndexing.ts` and thereafter only read,
   * never recomputed. It is the value the approve payload's
   * `excluded_record_indices` and the projection's `extracted_record_index`
   * are both expressed in, and it is what the worker excludes on.
   *
   * Optional because the shape is shared with fixtures and with the
   * pre-#6755 read path. Read it through `absoluteIndexOf` rather than
   * directly, so a missing value fails loudly instead of resolving to a
   * plausible wrong record.
   */
  record_index?: number;
}

export interface CoverageSchemaAnalysis {
  target_schema: TargetSchema;
  expected_fields: string[];
  unpopulated_fields?: Record<string, number>;
}

export interface FieldCoverage {
  field_name: string;
  is_populated: boolean;
  extraction_method: string;
  confidence: number;
}

export interface CoverageAnalysis {
  schema_analyses: CoverageSchemaAnalysis[];
  total_expected_fields: number;
  total_unpopulated_fields: number;
  field_level_coverage?: Record<string, FieldCoverage>;
  extraction_method_used?: string;
}

/**
 * Verdict of the extraction completeness reconciliation (#5767): the number of
 * member sections found in the raw document text vs. the number of distinct
 * claimants that actually loaded. Exists to make silent extraction loss visible
 * — a narrative/form-style report (one section per member) run through the
 * tabular extractor can drop whole member blocks with no other signal.
 */
export type CompletenessStatus = "passed" | "failed" | "unverifiable";

export interface CompletenessCheck {
  status: CompletenessStatus;
  /** Number of member sections counted in the raw document text. 0 when unverifiable. */
  expected_members: number;
  /** Number of distinct claimants that loaded. */
  loaded_claimants: number;
  /** Short, PHI-free explanation for the review surface. */
  detail?: string;
}

export interface ProcessingMetadata {
  processing_time_ms: number;
  ai_model_version?: string;
  document_type?: DocumentTransformationType;
  document_pages?: number;
  process_sheet_name?: string;
  extraction_method: ExtractionMethod;
  detected_headers?: string[];
  /** Extraction completeness reconciliation verdict (#5767). */
  completeness?: CompletenessCheck;
  /**
   * Per-schema record counts computed by the worker over the FULL record set
   * (#6755). Mirrors document_processing.RecordSummary on the Go side; see
   * utils/recordSummary.ts for the two quirks both sides preserve.
   *
   * Absent on transformations written before #6755 — the review surface falls
   * back to counting the loaded records, which is correct there because such a
   * document is loaded whole.
   */
  record_summary?: RecordSummaryPayload;
  /**
   * Row-level decisions the transformer made that change what will load
   * (#6921). Shape must match document_processing.TransformationRowDecisions.
   *
   * ABSENT means no transformer reported these — every transformation
   * finalized before #6921. The surface must show NOTHING rather than zero:
   * "0 rows skipped" asserts something about a parse nobody measured.
   *
   * Present with both counts zero is a real determination ("the transformer
   * ran and neither heuristic fired") and also shows nothing, for the opposite
   * reason. The two must not share a sentinel.
   *
   * HERE, not nested under record_summary, and the move was a !4681 review
   * finding rather than tidying. On the Go side RecordSummary is assigned
   * WHOLESALE by SummarizeRecords, so a field hung off it is reverted to
   * "absent" by any reorder — silently, because absent is a legal value. It
   * also does not exist at all on the failure path, where a run that dropped
   * 40 rows and then failed is exactly the run a reviewer needs the count
   * for: ProjectDocumentReview reads the latest transformation with no status
   * filter.
   *
   * Reading the OLD path (record_summary.row_decisions) now finds nothing, so
   * the notice disappears rather than lying — the safe direction, and the
   * reason the two repos could be moved in either order.
   */
  row_decisions?: TransformationRowDecisionsPayload;
}

/**
 * Server-computed record aggregates. Shape must match
 * document_processing.RecordSummary.
 */
export interface RecordSummaryPayload {
  /** Records per schema tab. The claimant entry is POST-deduplication. */
  counts_by_schema: Partial<Record<TargetSchema, number>>;
  /** Sum of counts_by_schema — a record carrying two schemas counts twice. */
  total_records: number;
  /**
   * DISTINCT records carrying their own validation_errors — a two-schema
   * record counts once, unlike total_records' deliberate bucket sum. This
   * figure is presented to the reviewer as "N records with errors" and gates
   * approval, so per-record is the only semantics matching the sentence
   * (#6755 review round 2).
   */
  records_with_errors: number;
  /**
   * Largest number of records sharing one claimant identity — the
   * PROJECTION'S identity (Go ClaimantWindowIdentity, the key
   * mergeClaimantRecords groups on), not the display-dedup key.
   *
   * LOAD-BEARING, not informational (#6755): a claimant's ytd_delta sums
   * paid_amount across ALL of that claimant's rows, so a WINDOWED review
   * projection is only correct when no claimant straddles the window edge. A
   * value of 1 guarantees that precisely because the counting and the merging
   * share one identity function. Above 1 both the service and the review tab
   * refuse to window rather than show an understated dollar figure.
   *
   * SERVER-COMPUTED ONLY. The client mirror (recordSummary.ts) reports NULL —
   * abstention is structurally distinct from a real value, because the
   * server's 0 is load-bearing in the OPPOSITE direction ("no claimants at
   * all, any window trivially safe"). A shared sentinel here would be the
   * same value-overload the totalKnown flag was added to remove (#6755 review
   * round 2). Every gate reads the server value and fails closed on
   * null/absent.
   */
  max_rows_per_claimant: number | null;
  /**
   * Which ClaimantWindowIdentity definition max_rows_per_claimant was counted
   * under. The summary is persisted once at finalize; if the identity changes,
   * stored values are stale evidence and can under-report. Gates require the
   * current version (CLAIMANT_WINDOW_IDENTITY_VERSION in projectionGate.ts)
   * and treat absent/mismatched like an absent summary. Absent on summaries
   * written before versioning and on the client mirror's abstention.
   */
  identity_version?: number;
  /**
   * Which carrier-reported YTD components the DOCUMENT maps, unioned across
   * every record, plus the ADR-077 §8 double-count signal (#6903).
   *
   * ABSENT means "no worker computed this" — any transformation finalized
   * before #6903 — and the review surface must then FAIL OPEN and show the
   * paid-through-date control. That is the opposite of every other gate on
   * this payload, deliberately:
   *
   *   - a visible control on a document with no YTD is a blank field the
   *     reviewer ignores;
   *   - a hidden control on a document that HAS YTD silently hands claims
   *     authority to the carrier-reported side (ADR-051), and the reviewer is
   *     never told a decision was made for them.
   *
   * Present-with-all-flags-false is a real determination ("computed; nothing
   * mapped") and is NOT the same as absent. The two must never be conflated —
   * which is why this is a nested object rather than flat booleans on this
   * interface, where `false` and "not sent" would be indistinguishable.
   */
  reported_ytd?: ReportedYTDPayload;
  /**
   * Latest claim_item paid_date in the WHOLE document, YYYY-MM-DD (#7439).
   * Server-computed at finalize over the complete set; the client mirror
   * abstains (omits it) because a page max is exactly the partial answer this
   * replaces. Absent on summaries written before the field shipped.
   */
  latest_paid_date?: string | null;
}

/**
 * Document-level carrier-reported YTD picture. Shape must match
 * document_processing.ReportedYTDSummary (the embedded ReportedScope fields
 * promote, so this is flat).
 */
export interface ReportedYTDPayload {
  /** ytd_medical_paid parsed to a usable value somewhere on the document. */
  has_medical: boolean;
  /** ytd_rx_paid parsed to a usable value somewhere on the document. */
  has_rx: boolean;
  /** ytd_total_paid parsed to a usable value somewhere on the document. */
  has_total: boolean;
  /**
   * ADR-077 §8: ANY record reports a total not broken out into a separate Rx
   * figure that exceeds its medical portion. A per-record value COMPARISON, so
   * it cannot be reconstructed from the three flags above — one offending
   * record among thousands is the risk.
   */
  combined_total_risk: boolean;
}

/**
 * Counts of row-level decisions the transformer made that change what loads.
 *
 * Both were already computed and both went only to a log line, so a reviewer
 * approving a document could not know either happened (#6921).
 */
export interface TransformationRowDecisionsPayload {
  /**
   * Rows DROPPED as subtotal/total lines (#5727). The skip is right — carrier
   * files interleave per-claimant totals among the detail lines and loading
   * them double-counts — but it is a heuristic on SHAPE, so a genuine claim
   * line with only amounts filled is indistinguishable. When it misjudges,
   * that row's money never loads and nothing reports a missing row.
   */
  skipped_aggregate_rows: number;
  /**
   * Rows whose blank claimant identity was filled from the row above
   * (#6487/#5782). Also right — vendors lay out a claimant-header row followed
   * by claim lines with the identity columns blank — but the reviewer sees a
   * claimant name on a row where the file had none, and attribution is what
   * every downstream YTD and deductible figure is keyed on.
   */
  forward_filled_rows: number;
}

export interface DocumentTransformation extends CommonFields, AuditFields, DocumentRef, PartnerRef {
  status: DocumentTransformationStatus;
  extracted_records?: ExtractedRecord[];
  coverage_percentage?: number;
  coverage_analysis?: CoverageAnalysis;
  processing_metadata?: ProcessingMetadata;
  target_schemas?: TargetSchema[];
  validation_errors?: ExtractedRecordError[];
  transformation_template_id_ref?: string;
}

// ==================== REVIEW PROJECTION ====================
// Response shape for POST /documents/{documentId}/review-projection. Matches
// DocumentReviewProjection in apps/pkg/services/review_projection.go.
//
// Numeric fields are serialized by Go's decimal package as JSON numbers (not
// strings) when they fit — observed shape confirms this. Typed as `number`
// for ergonomic consumption; precision is sufficient for display (we do no
// client-side math beyond rendering).

/**
 * Per-row match outcome from the document review projection.
 *
 * - "new": no existing claimant matched — approving creates a new record.
 * - "matched": existing claimant resolved on member_id (or strict name).
 * - "matched_backfill": existing claimant resolved by name on a row whose
 *   member_id was NULL; approving will write the incoming member_id onto
 *   the existing row (one-shot soft-merge dedup). The reviewer should verify
 *   the merge is correct before approving.
 */
export type MatchStatus = "new" | "matched" | "matched_backfill";

/**
 * Source of the applicable deductible for a claimant. The projection keeps the
 * resolver's provenance (Feature #3502), so the claimant-specific sources of
 * the effective deductible can appear too. "" = unknown (shown as "applicable").
 */
export type DeductibleSource =
  | "lasered"
  | "specific"
  | "family"
  | "conditional_laser"
  | "review_decision"
  | "modification"
  | "contract_laser"
  | "";

/** Per-claimant forecast of what approving this document will do. */
export interface ClaimantProjection {
  /** 0-based index into DocumentTransformation.extracted_records */
  extracted_record_index: number;
  claimant_name: string;
  match_status: MatchStatus;
  existing_claimant_id: string | null;
  ytd_before: number;
  ytd_delta: number;
  ytd_after: number;
  will_create_sl_claim: boolean;
  /** UUID of the open SL claim that would be updated; null when not updating. */
  will_update_sl_claim_id: string | null;
  /**
   * Human-readable identifier (e.g. "SLC-AB12345") of the SLC that would be
   * updated. Paired with will_update_sl_claim_id — both set together or both
   * absent. Used by the forecast chip so reviewers see which submission is
   * being amended without copying a UUID.
   */
  will_update_sl_claim_aegle_id?: string;
  /** Populated when the policy has an enabled family deductible endorsement. */
  family_group_id?: string | null;
  family_ytd_before?: number | null;
  family_ytd_after?: number | null;
  /**
   * Normalized relationship ("self" | "spouse" | "dependent" | "other"),
   * echoing the loader's alias map. Informational context for the frontend.
   */
  claimant_relationship?: string | null;
  /**
   * True when the claimant sits on a family-deductible policy but isn't in a
   * family unit yet. The load pipeline holds these claimants, so the forecast
   * shows no SL claim regardless of YTD. Frontend uses this to flag the chip
   * and drive the "form a family unit" prompt on the claimant page.
   */
  is_pending_family_link: boolean;
  /**
   * Carrier-reported subscriber_id grouping this claimant with the rest of
   * their family unit. Rows sharing a value belong to the same family — the
   * UI uses it as a stable grouping key for the Family column and row tint.
   */
  family_subscriber_id?: string | null;
  /**
   * Display name of the EE anchoring this family (resolved from the in-file
   * self row or a pre-existing self claimant in the DB). Shown as the Family
   * column label. Empty/null when the EE can't be identified — in that case
   * is_pending_family_link should be true and the reviewer forms the unit
   * manually via the banner.
   */
  family_subscriber_name?: string | null;
  /**
   * The deductible THIS claimant is measured against (#7435): the family
   * amount on a family-deductible policy, otherwise the claimant's laser or
   * the policy's specific. 0 / "" when the policy has neither. The document-
   * level fields of the same name are the first claimant's values.
   */
  applicable_deductible?: number;
  deductible_source?: DeductibleSource;
}

/**
 * Document-level advisory observation surfaced by the projection endpoint.
 * Rendered as a dismissible chip/banner in the review header. Never blocks
 * approval — reviewers can acknowledge and proceed.
 */
export interface ProjectionWarning {
  /** Stable identifier for the warning kind; frontend can branch on this if needed. */
  kind: string;
  /** Reviewer-facing text. */
  message: string;
  /** Optional structured context the frontend can use for drill-downs. */
  details?: Record<string, unknown>;
}

/**
 * The policy the projection ran against (#7462): the one behind
 * applicable_deductible, family_deductible, rx_carve_out and the
 * items_outside_policy_period warning. The header labels the document with it
 * (carrier + year), so "this policy" means one thing on the screen.
 */
export interface ResolvedPolicy {
  policy_id: string;
  aegle_id: string;
  /** Stop-loss carrier label; empty/absent → render "No Carrier" like the policies list. */
  carrier_name?: string;
  /** YYYY-MM-DD */
  effective_start_date: string;
  /** YYYY-MM-DD */
  effective_end_date: string;
}

export interface DocumentReviewProjection {
  applicable_deductible: number;
  deductible_source: DeductibleSource;
  family_deductible?: number | null;
  /**
   * True on a WINDOWED projection of a family-deductible policy (#6755 review
   * round 2): the family pool sums ytd across MEMBERS, and
   * max_rows_per_claimant === 1 proves no claimant straddles the window edge —
   * not that no family does. Rather than compute a pool from a partial member
   * set (understated total, breach can read false), the server suppresses all
   * family figures and per-row SL verdicts and sets this so the surface can
   * say why they're absent. The pool is evaluated in full at load time.
   */
  family_rollup_unavailable?: boolean;
  claimants: ClaimantProjection[];
  /**
   * Per-claimant reimbursement rollups. Populated only for documents whose
   * record_type is "reimbursements"; empty for stoploss-claim docs.
   */
  reimbursement_summaries?: ReimbursementSummary[];
  /**
   * Per-row claim_item forecast for stoploss-claim (claims) documents — whether
   * approving inserts a new claim line or folds into one already on file. Keyed
   * by extracted_record_index. Absent for reimbursement docs and for claims docs
   * with no claim_item records. (AB#4456)
   */
  claim_item_forecasts?: ClaimItemRowForecast[];
  /** Advisory observations — present only when at least one check fired. */
  warnings?: ProjectionWarning[];
  /**
   * Reviewer-confirmable default as-of (paid-through) date parsed from the
   * document's "Paid Date … through …" header during processing (ADR-051), as
   * YYYY-MM-DD. The review UI pre-fills its editable as-of field from this;
   * absent when no Paid-Date range was found — the reviewer can still enter one
   * manually.
   */
  default_reported_as_of?: string | null;
  /**
   * Where `default_reported_as_of` came from. "header" = read off the document;
   * "prior_month_end" = synthesized (last day of the month before upload) with
   * no basis in the file. The control must not describe the second as the first.
   */
  default_reported_as_of_source?: "header" | "prior_month_end" | null;
  /**
   * The resolved policy's effective Rx carve-out flag (ADR-077 §2). When true,
   * the review UI scope-labels the as-of control per claim type; false/absent =>
   * bundled => the control renders unchanged. Doc-level (reflects the
   * projection's resolved policy).
   */
  rx_carve_out?: boolean | null;
  /**
   * Latest claim_item paid_date in the WHOLE document, YYYY-MM-DD (#7439) —
   * never just the projected window. The paid-through "before the latest paid
   * claim" check reads this; absent means UNKNOWN (no claim line carries a
   * date, or a windowed request against a pre-#7439 summary) and the surface
   * falls back to scanning the loaded records with the partial disclosure.
   */
  latest_paid_date?: string | null;
  /** The policy this projection ran against (#7462). Absent when the client has no policy. */
  resolved_policy?: ResolvedPolicy | null;
  /** The paid and incurred windows claim lines were judged by (#7436). Absent when the client has no policy. */
  policy_windows?: PolicyWindows | null;
}

/**
 * Read-only preview of a per-line claim row associated with a parent
 * reimbursement. Composed from EOR per-line records the loader filters out
 * before insert. Surfaced under each reimbursement so reviewers can audit
 * line counts and adjustment/denial sums at review time. After approve
 * these are gone — the source PDF is the long-term record. AB#4455.
 *
 * Both `requested_amount` and `reimbursement_amount` are present because a
 * template can only map one source field to a given target — different
 * templates route the per-line dollar value into either slot depending on
 * whether `reimbursement_amount` is already taken by a header-level mapping.
 * The line-items table renders whichever is populated.
 */
export interface LineItemPreview {
  extracted_record_index: number;
  provider?: string;
  service_start_date?: string;
  service_end_date?: string;
  billed_amount?: number;
  requested_amount?: number;
  reimbursement_amount?: number;
  adjustment_amount?: number;
  reference_number?: string;
}

/**
 * Per-row disposition for a single extracted claim_item record on a claims
 * (stoploss-claim) document — mirrors ClaimItemRowForecast in
 * apps/pkg/services/review_projection.go. Answers "is this claim line new, or
 * already on file?" using the same natural-key dedup the loader runs on approve.
 *
 * `operation`:
 *   - "insert" — no matching line on file; approving creates a new claim_item.
 *   - "update" — a line matching this row's natural key is already on file;
 *     approving refreshes it in place. (Covers both the loader's additive merge
 *     and its group-replacement path — from the reviewer's view both mean the
 *     line is already on file.)
 *
 * "Excluded" is intentionally not modeled — exclusions act in the
 * breach/deductible layer, not at claim_item load.
 */
export interface ClaimItemRowForecast {
  extracted_record_index: number;
  operation: "insert" | "update";
  is_duplicate_on_file: boolean;
  duplicate_of_id?: string | null;
  /**
   * False when the line fails the loader's paid-date or service-date window
   * check (#7436): it loads but never counts toward the deductible, a
   * stop-loss claim or the aggregate. Absent on an older API → treat as true.
   */
  counts_toward_deductible?: boolean;
  /**
   * The failed checks: paid_date_outside_policy, service_date_outside_incurred_period,
   * and under the basis engine missing_paid_amount / missing_paid_date / missing_service_date
   * (one reason per line there, the view's first failing check).
   */
  ineligible_reasons?: string[];
}

/**
 * The two date windows the loader checks claim lines against (#7436), from
 * the same sources it reads at load: the policy term and the specific
 * benefit's incurred period.
 */
export interface PolicyWindows {
  /** YYYY-MM-DD */
  paid_start: string;
  paid_end: string;
  /** False when the policy has no specific benefit with incurred dates — the loader skips the service-date check. */
  incurred_check_applies: boolean;
  incurred_start?: string;
  incurred_end?: string;
  /** N = 999: no incurred lookback — only incurred_end bounds the window and incurred_start is omitted. */
  incurred_no_lookback?: boolean;
  contract_basis?: string;
  /** Which accumulation engine the windows came from (ADR-081 D5 flag on → deductible_basis). */
  engine?: "deductible_basis" | "legacy";
}

/**
 * Confidence with which the projection (and load pipeline) linked a
 * reimbursement row to a stoploss claim. Mirrors
 * services.ReimbursementLinkConfidence in coronis-apps.
 */
export type ReimbursementLinkConfidence = "direct_id" | "sole_open" | "date_match" | "ambiguous" | "none" | ""; // empty when IsDuplicateOnFile (linkage not meaningful for duplicates)

/**
 * Per-row forecast inside a ReimbursementSummary. Each row corresponds to a
 * single reimbursement extracted record. Ordered as they appear in the doc.
 */
export interface ReimbursementRowForecast {
  extracted_record_index: number;
  requested_amount: number;
  reimbursement_amount: number;
  /**
   * True when a reimbursement matching the row's natural key already exists
   * in the DB. Approving will skip insertion. The link fields are empty in
   * this case — the existing row's linkage is what matters.
   */
  is_duplicate_on_file: boolean;
  duplicate_of_id?: string | null;
  /** Stoploss claim approval will attach to. Null when ambiguous/none. */
  linked_sl_claim_id?: string | null;
  link_confidence: ReimbursementLinkConfidence;
  link_reason?: string;
  /** Populated when link_confidence === "ambiguous" — reviewer picks. */
  ambiguous_candidates?: string[];
  /**
   * Per-line claim items attached to this parent reimbursement. Sourced
   * from the EOR per-line records the loader filters out before insert.
   * Rendered as a nested table under the parent so reviewers can audit
   * line counts + adjustment/denial sums during review. Not stored after
   * approve. AB#4455.
   */
  line_items?: LineItemPreview[];
}

/**
 * Per-claimant reimbursement rollup. The grid renders one top-row per
 * claimant; this struct describes what approving will do for that claimant's
 * reimbursement set.
 */
export interface ReimbursementSummary {
  /** First extracted record index for this claimant — drives grid → forecast lookup. */
  extracted_record_index: number;
  claimant_name: string;
  match_status: MatchStatus;
  existing_claimant_id?: string | null;
  /** Per-row forecasts, in document order. */
  rows: ReimbursementRowForecast[];
  /** Aggregate counts derived from rows. */
  new_reimbursement_count: number;
  duplicate_on_file_count: number;
  linked_count: number;
  unlinked_count: number;
  ambiguous_link_count: number;
  /** Sums across all rows in the summary. */
  total_requested_amount: number;
  total_reimbursement_amount: number;
}

// ---------------------------------------------------------------------------
// Multi-client ("book of business") review projection (#5321 / ADR-057)
// ---------------------------------------------------------------------------
// Returned by the SAME POST /documents/{id}/review-projection endpoint for a
// multi-client document (object_metadata.is_multi_client), instead of the
// single-client DocumentReviewProjection. Nested client → claimant → rows.

/** Per-row resolution outcome (mirrors the backend RecordResolutionStatus). */
export type BOBResolutionStatus =
  | "resolved"
  | "resolved_unlinked"
  | "quarantine_no_client"
  | "quarantine_no_claimant"
  | "quarantine_ambiguous_client"
  | "quarantine_multi_policy";

/** One extracted reimbursement row's forecast within a claimant group. */
export interface BOBRowForecast {
  extracted_record_index: number;
  resolution_status: BOBResolutionStatus;
  resolution_reason?: string;
  policy_linked: boolean;
  requested_amount: number;
  reimbursement_amount: number;
}

/** One distinct claimant within a client group. */
export interface BOBClaimantForecast {
  claimant_name: string;
  /** "new" | "matched" | "matched_backfill". */
  match_status: MatchStatus;
  existing_claimant_id?: string | null;
  rows: BOBRowForecast[];
}

/** One resolved (or to-be-created) client and its claimants. */
export interface ClientReviewGroup {
  client_name: string;
  /** "matched" (existing client) | "new" (will be created) | "unresolved" (no client name). */
  match_status: "matched" | "new" | "unresolved";
  existing_client_id?: string | null;
  claimants: BOBClaimantForecast[];
  total_rows: number;
  resolved_rows: number;
  unlinked_rows: number;
  quarantined_rows: number;
  total_requested_amount: number;
  total_reimbursement_amount: number;
}

/** Nested forecast for a multi-client document. */
export interface MultiClientReviewProjection {
  clients: ClientReviewGroup[];
  /**
   * Count of rows with no claimant (ADR-057 #1: claimant is the hard load
   * requirement). They can't load and don't create a client — excluded from the
   * client groups and surfaced separately in the review.
   */
  no_claimant_rows: number;
  warnings?: ProjectionWarning[];
}
